/**
 * Контракт «команды объекта» — Блок 2 «Трекер объектов», Ф2 · В2b.
 *
 * ─── ЧТО ЭТО ЗА СУЩНОСТЬ ──────────────────────────────────────────────────
 *
 * «Команда объекта» — не таблица, а ПРОЕКЦИЯ поверх двух: `ProjectParticipant`
 * (кто вообще на объекте) и `Work` вида `PROJECT_WORK` (кто что ведёт и в
 * какой период). Один маршрут заводит обе строки разом, потому что порознь они
 * бессмысленны: участник без работы не может отчитаться о часах, а работа без
 * участия ссылается на человека, которому объект не виден.
 *
 * ─── ПОЧЕМУ СВОЙ ФАЙЛ, А НЕ ДОБАВКА В `work.ts` ───────────────────────────
 *
 * `work.ts` — контракт САМОЙ работы (одна строка, оба вида). Здесь — контракт
 * ЭКРАНА команды: сгруппированный по разделам список плюс отдельная секция
 * «участники без работ». Слив их в один файл, следующая волна не смогла бы
 * поменять форму экрана, не задев форму сущности.
 *
 * ─── ЧАСОВ В ОТВЕТЕ НЕТ ───────────────────────────────────────────────────
 *
 * `hoursEstimate` не приходит здесь ровно по той же причине, по которой его
 * нет в `WorkResponse`: поле МАСКИРУЕТСЯ даже в снимке аудита
 * (`SENSITIVE_FIELDS.work`), и маршрут, отдающий его хоть суммой, хоть
 * поштучно, обошёл бы маскирование арифметикой.
 */
import { z } from 'zod';
import { WorkStatus } from './work';

const UuidSchema = z.string().uuid();

/** `YYYY-MM-DD` — та же синтаксическая проверка, что в `work.ts`. */
const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата должна быть в формате ГГГГ-ММ-ДД');

/**
 * Название работы в запросе — 1..300 после обрезки, `''` → `undefined`.
 *
 * ЗЕРКАЛО `TitleSchema` из `work.ts`, и повторено оно намеренно: там схема
 * приватная, а экспортировать её значило бы связать форму экрана команды с
 * формой карточки работы. Пустое название — ЛЕГАЛЬНОЕ состояние: сервер
 * оставляет `NULL`, а интерфейс показывает актуальное имя раздела из
 * каталога (план фазы §6.6). Без нормализации `''` пустое поле формы (а
 * `<input>` всегда даёт `''`) упиралось бы в «минимум 1 символ».
 */
const TeamTitleSchema = z.preprocess((v) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}, z.string().min(1).max(300, 'Название не длиннее 300 символов').optional());

/**
 * `POST /projects/:projectId/team`.
 *
 * `projectId` в теле НЕТ — он в пути. `.strict()` отвергает и его, и `kind`:
 * вид работы здесь всегда `PROJECT_WORK`, и возможность прислать `TASK` дала
 * бы способ завести задачу в обход правил делегирования модуля работы.
 */
export const AddTeamMemberRequestSchema = z
  .object({
    userId: UuidSchema,
    sectionId: UuidSchema,
    startDate: IsoDateSchema,
    dueDate: IsoDateSchema,
    title: TeamTitleSchema,
  })
  .strict()
  .refine((v) => v.dueDate >= v.startDate, {
    message: 'Срок не может быть раньше начала',
    path: ['dueDate'],
  });
export type AddTeamMemberRequest = z.infer<typeof AddTeamMemberRequestSchema>;

/**
 * Ответ `POST /projects/:projectId/team` (Блок 2 · Ф7 · В11-d).
 *
 * Два идентификатора — как были; два множества — новые, и это ТЕ ЖЕ множества,
 * что у ответа мутации работы (`WorkMutationResponseSchema`): кому в этой
 * мутации поставлена строка очереди и у кого из них нет привязки бота. Их
 * читает пометка постановщику «бот не привязан» в окне назначения (Р-132).
 *
 * ⚠️ `notBoundUserIds` — ПОДМНОЖЕСТВО `queuedUserIds`. Оба пусты, когда
 * руководитель назначил работу сам себе: уведомление о собственном действии
 * не ставится (Р-64).
 */
export const AddTeamMemberResponseSchema = z.object({
  workId: UuidSchema,
  participantId: UuidSchema,
  queuedUserIds: z.array(UuidSchema),
  notBoundUserIds: z.array(UuidSchema),
});
export type AddTeamMemberResponse = z.infer<typeof AddTeamMemberResponseSchema>;

/** Одна работа в составе команды. */
export const ProjectTeamWorkSchema = z.object({
  workId: UuidSchema,
  userId: UuidSchema,
  fullName: z.string(),
  departmentId: z.number().int().nullable(),
  /** Пустое название — легальное состояние; интерфейс показывает имя раздела. */
  title: z.string().nullable(),
  startDate: IsoDateSchema,
  dueDate: IsoDateSchema,
  status: z.nativeEnum(WorkStatus),
  // Поля `stage` здесь БОЛЬШЕ НЕТ: стадии ПД/РД сняты заказчиком 11.08.2026
  // (DECISIONS.md, Р-1) вместе с колонкой — миграция 053. Вместе с полем ушёл
  // и критерий В3 «стадия видна у работы и фильтруется», ради которого поле
  // сюда и добавлялось.
  /**
   * ВЫЧИСЛЯЕМОЕ поле: колонки срочности нет и не будет. Считается сервером от
   * его собственного «сегодня» (`Europe/Moscow`), а не браузером: с 00:00 до
   * 03:00 MSK «завтра» у клиента и сервера разное.
   */
  urgent: z.boolean(),
  version: z.number().int().nonnegative(),
});
export type ProjectTeamWork = z.infer<typeof ProjectTeamWorkSchema>;

/** Раздел с его работами — так экран рисует дерево. */
export const ProjectTeamSectionSchema = z.object({
  sectionId: UuidSchema,
  /** АКТУАЛЬНОЕ имя из справочника, а не снимок (KD-5 отменён). */
  sectionName: z.string(),
  /**
   * Имя группы справочника, под которой лежит раздел; `null` — группы нет
   * (Ф3 · В3, T3.6). Разбор трёх законных источников `null` — в докблоке
   * `SectionResponseSchema.groupName`, повторять его здесь не нужно.
   *
   * ⚠️ ДЕРЕВО КАРТОЧКИ ОБЪЕКТА СТРОИТСЯ НЕ ПО ЭТОМУ ОТВЕТУ, и это важно знать
   * до того, как поле покажется избыточным. `GET /projects/:id/team` — проекция
   * поверх `Work`: раздел без единой живой работы в `sections[]` НЕ ПОПАДАЕТ
   * вовсе (`project-team.service.ts` перебирает работы, а не разделы). Узлы
   * дерева берутся из `GET /projects/:id/sections`, а работы прикрепляются
   * сюда по `sectionId`. Поле стоит здесь для потребителей САМОЙ проекции
   * (В5 «Моя работа», Ф4) и чтобы два ответа не разошлись в том, что такое
   * «группа раздела».
   */
  groupName: z.string().nullable(),
  /** Идентификатор группы — ключ группировки и раскрытия. Разбор — в
   *  `SectionResponseSchema.groupId`: по имени узлы сливаются, ссылки гниют. */
  groupId: UuidSchema.nullable(),
  /**
   * Пункт справочника, из которого раздел заведён; `null` — пункта нет.
   *
   * ⚠️ ЗАЧЕМ ОН ЗДЕСЬ, ЕСЛИ ЕСТЬ `groupId`. Вдвоём они РАЗЛИЧАЮТ три случая,
   * которые поодиночке сливаются в один и выглядят как поломка:
   *   - `catalogItemId ≠ null`, `groupId = null` — раздел заведён из пункта
   *     ВЕРХНЕГО уровня, то есть из самой группы. В справочнике такой пункт
   *     лежит на верхнем уровне, и в дереве объекта раздел из него обязан
   *     лежать там же — рядом с узлами-группами, а не в служебном узле;
   *   - `catalogItemId ≠ null`, `groupId ≠ null` — обычный раздел под группой;
   *   - `catalogItemId = null` — раздела нет в справочнике вовсе: заведён
   *     свободным текстом либо пункт физически удалён (`onDelete: SetNull`).
   *     Только этот случай служебный.
   *
   * Первый случай на стенде ПРЕОБЛАДАЕТ: из восьми пунктов, на которые
   * ссылаются живые разделы, пять — групповые строки. Без этого поля все они
   * показывались бы в узле «раздела нет в справочнике», что прямая неправда.
   *
   * ⚠️ Раздел от ПОДпункта, чью группу удалили, придёт сюда как первый случай
   * (`parentId` обнулён тем же `SetNull`) — и это ВЕРНО: в справочнике такой
   * пункт теперь и есть верхнеуровневый. Правило «дерево повторяет справочник»
   * выдерживает все четыре состояния, и отдельной ветки не нужно.
   */
  catalogItemId: UuidSchema.nullable(),
  works: z.array(ProjectTeamWorkSchema),
});
export type ProjectTeamSection = z.infer<typeof ProjectTeamSectionSchema>;

/** Участник, у которого на объекте нет НИ ОДНОЙ живой работы. */
export const ProjectTeamIdleParticipantSchema = z.object({
  userId: UuidSchema,
  fullName: z.string(),
  departmentId: z.number().int().nullable(),
});
export type ProjectTeamIdleParticipant = z.infer<typeof ProjectTeamIdleParticipantSchema>;

/**
 * `GET /projects/:projectId/team`.
 *
 * Секция «участники без работ» — ОТДЕЛЬНАЯ, а не строка с пустым разделом:
 * это разные состояния. Человек без работы на объекте есть (и виден в
 * ростере, и может быть назначен), но часов по нему записать нельзя, и экран
 * обязан показывать это как незавершённое действие, а не как норму.
 *
 * `today` отдаётся вместе с данными, чтобы клиент мог пересчитать срочность
 * ТЕМ ЖЕ «сегодня», каким её считал сервер.
 */
export const ProjectTeamResponseSchema = z.object({
  sections: z.array(ProjectTeamSectionSchema),
  participantsWithoutWork: z.array(ProjectTeamIdleParticipantSchema),
  today: IsoDateSchema,
});
export type ProjectTeamResponse = z.infer<typeof ProjectTeamResponseSchema>;
